// @ts-nocheck
import React, { useEffect, useState, useRef } from 'react';
import { useUpload } from '../contexts/UploadContext';
import { useAuth } from '../contexts/AuthContext';
import { HydratedPost, Post } from '../types';
import { PostVideoSharePreview } from './PostVideoSharePreview';

export const BackgroundVideoRenderer: React.FC = () => {
    const { startGeneration, updateGenerationProgress, completeGeneration, failGeneration } = useUpload();
    const { usersData } = useAuth();
    const [currentTask, setCurrentTask] = useState<{
        post: Post | HydratedPost;
        mediaUrl: string;
        mediaType: string;
        isProfileShare?: boolean;
    } | null>(null);

    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const recorderRef = useRef<MediaRecorder | null>(null);
    const [audioStream, setAudioStream] = useState<MediaStream | null>(null);
    const [isRecording, setIsRecording] = useState(false);
    const recordingActiveRef = useRef(false);

    useEffect(() => {
        const handleStart = (e: CustomEvent) => {
            if (currentTask) {
                console.warn("Background generation already in progress.");
                return;
            }
            setCurrentTask(e.detail);
            setIsRecording(true);
            recordingActiveRef.current = false;
        };
        window.addEventListener('start-background-generation', handleStart as EventListener);
        return () => window.removeEventListener('start-background-generation', handleStart as EventListener);
    }, [currentTask]);

    useEffect(() => {
        let timeoutId: NodeJS.Timeout;
        let progressInterval: NodeJS.Timeout;

        if (isRecording && !recordingActiveRef.current && currentTask) {
            const checkAndStart = () => {
                if (canvasRef.current) {
                    recordingActiveRef.current = true;
                    try {
                        const stream = canvasRef.current.captureStream(30);
                        const tracks = [...stream.getVideoTracks()];
                        if (audioStream) {
                            tracks.push(...audioStream.getAudioTracks());
                        }

                        const finalStream = new MediaStream(tracks);
                        const supportedTypes = [
                            'video/webm;codecs=vp9,opus',
                            'video/webm;codecs=vp8,opus',
                            'video/mp4;codecs=avc1,mp4a',
                            'video/webm'
                        ];
                        const mimeType = supportedTypes.find(type => MediaRecorder.isTypeSupported(type)) || 'video/webm';

                        const recorder = new MediaRecorder(finalStream, {
                            mimeType,
                            videoBitsPerSecond: 5000000
                        });
                        recorderRef.current = recorder;

                        const chunks: Blob[] = [];
                        recorder.ondataavailable = (e) => {
                            if (e.data && e.data.size > 0) chunks.push(e.data);
                        };

                        recorder.onstop = () => {
                            const blob = new Blob(chunks, { type: mimeType });
                            const ext = mimeType.includes('mp4') ? 'mp4' : 'webm';
                            const fileName = `goodlyfe_share_${Date.now()}.${ext}`;
                            const postUrl = `https://goodlyfe.app/post/${currentTask.post.id}`;
                            const shareText = `Check out this post from @${currentTask.post.author} on GoodLyfe! ${postUrl}`;
                            completeGeneration(blob, fileName, mimeType, postUrl, shareText);
                            
                            // Reset state
                            setCurrentTask(null);
                            setIsRecording(false);
                            recordingActiveRef.current = false;
                            setAudioStream(null);
                        };

                        recorder.start(100);

                        let elapsed = 0;
                        const duration = currentTask.mediaType === 'gif' ? 10000 : 15000;
                        
                        progressInterval = setInterval(() => {
                            elapsed += 100;
                            updateGenerationProgress((elapsed / duration) * 100);
                        }, 100);

                        // Stop automatically after duration
                        timeoutId = setTimeout(() => {
                            if (recorder.state === 'recording') {
                                recorder.stop();
                            }
                            clearInterval(progressInterval);
                        }, duration);

                    } catch (error) {
                        console.error('Failed to capture stream:', error);
                        failGeneration('Stream capture failed');
                        setCurrentTask(null);
                        setIsRecording(false);
                        recordingActiveRef.current = false;
                    }
                } else {
                    timeoutId = setTimeout(checkAndStart, 200);
                }
            };
            
            // Give the preview component 1000ms to load media before trying to capture
            timeoutId = setTimeout(checkAndStart, 1000);
        }

        return () => {
            clearTimeout(timeoutId);
            clearInterval(progressInterval);
        };
    }, [isRecording, currentTask, audioStream, completeGeneration, failGeneration, updateGenerationProgress]);

    if (!currentTask) return null;

    const authorData = usersData[currentTask.post.author];
    const mediaItem = currentTask.post.content.media?.find((m: any) => m.url === currentTask.mediaUrl);
    const isBordered = currentTask.post.postcardStyle === 'bordered' || mediaItem?.fit === 'horizontal' || mediaItem?.fit === 'vertical';
    const bgMode = isBordered ? 'border' : 'borderless';
    
    // We render PostVideoSharePreview invisibly so it can draw to canvasRef
    // without the user seeing it blocking the screen
    return (
        <div style={{ position: 'fixed', left: '-10000px', top: '-10000px', pointerEvents: 'none' }}>
            <PostVideoSharePreview
                post={currentTask.post}
                authorData={authorData}
                postBlurOpacity={authorData?.postBlurOpacity || 80}
                canvasRef={canvasRef}
                startTime={0}
                endTime={currentTask.mediaType === 'gif' ? 10 : 15}
                mediaDuration={currentTask.mediaType === 'gif' ? 10 : 15}
                onAudioStreamReady={setAudioStream}
                mediaIndex={currentTask.post.content.media?.findIndex((m: any) => m.url === currentTask.mediaUrl) || 0}
                isPreviewing={false}
                imageBgMode={bgMode}
            />
        </div>
    );
};
